/**
 * UserTable Component
 * Displays paginated list of users with actions
 * 
 * IMPORTANT: Displays total count reflecting applied filters
 * as per user requirement: "all lists should display the totals count,
 * if they are filtered, should display the total number of the rows
 * from that applied filter"
 * 
 * Features:
 * - Sticky header that remains visible during scroll
 * - Sticky footer showing total count
 * - Internal scrolling (max-height with overflow)
 */

import { Edit2, Trash2, Users } from 'lucide-react';
import { formatDate } from '@/shared/utils/date-formatter';
import type { AdminUser } from '../types/admin-user.type';
import { UserTableSkeleton } from './UserTableSkeleton';

type UserTableProps = {
  users: AdminUser[];
  totalCount: number;
  isFiltered?: boolean;
  onEdit: (userId: string) => void;
  onDelete: (userId: string) => void;
  /**
   * Optional. When provided, an admin-only "Manage sub-delegates" action
   * renders for every non-`SUB_DELEGATE` row (no sub-sub-delegation, per the
   * Workspace Sub-Delegation design). Hidden when undefined.
   */
  onManageSubDelegates?: (userId: string) => void;
  isLoading?: boolean;
  maxHeight?: string;
};

/**
 * User table component displaying user list with actions
 * 
 * @param users - Array of users to display
 * @param totalCount - Total number of users (reflects filters if applied)
 * @param isFiltered - Whether filters are applied
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 * @param isLoading - Whether data is loading
 * 
 * Features:
 * - Displays name, email, role, status, join date, searches, saved providers
 * - Shows total count (filtered or unfiltered)
 * - Edit and delete actions per row
 * - Role and status badges with color coding
 * - Formatted dates
 * - Empty state message
 */
export function UserTable({
  users,
  totalCount,
  isFiltered = false,
  onEdit,
  onDelete,
  onManageSubDelegates,
  isLoading = false,
  maxHeight = 'calc(100vh - 250px)',
}: UserTableProps) {
  // Show loading skeleton
  if (isLoading) {
    return <div data-testid="user-table-skeleton"><UserTableSkeleton /></div>;
  }

  // Empty state
  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-400 mb-2" data-testid="empty-state-illustration">
          <svg
            className="h-12 w-12 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            data-testid="empty-state-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        </div>
        {isFiltered ? (
          <>
            <p className="text-gray-500 font-medium">No users match your filters</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your filters or search terms
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-500 font-medium">No users found</p>
            <p className="text-gray-400 text-sm mt-1">
              Get started by creating your first user
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-gray-200 rounded-md bg-white">
      {/* Sticky Header - Total count display */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="text-sm">
          {isFiltered ? (
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900">
                {users.length.toLocaleString('en-US')}
              </span>
              {' '}of{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString('en-US')}
              </span>
              {' '}users{' '}
              <span className="text-[#026857] font-medium">(Filtered)</span>
            </span>
          ) : (
            <span>
              Total{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString('en-US')}
              </span>
              {' '}
              {totalCount === 1 ? 'user' : 'users'}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable table container */}
      <div 
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Searches
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Saved
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="sticky right-0 z-20 bg-gray-50 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => {
              // Compute display name from firstName/lastName or use name field
              const displayName = user.name || `${user.firstName} ${user.lastName || ''}`.trim();
              
              return (
                <tr
                  key={user.id}
                  className="group hover:bg-gray-50 transition-colors"
                >
                  {/* Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">
                        {displayName}
                      </div>
                      {user.lastLoginAt && (
                        <div className="text-xs text-gray-500">
                          Last login: {formatDate(user.lastLoginAt)}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.email.toLowerCase()}
                    </div>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {user.role}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {user.isActive ? user.accountStatus : 'INACTIVE'}
                  </td>

                  {/* Searches Count */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.searchesCount ?? 0}
                  </td>

                  {/* Saved Providers Count */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.savedProvidersCount ?? 0}
                  </td>

                  {/* Joined Date */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>

                  {/* Actions */}
                  <td className="sticky right-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-4 whitespace-nowrap text-right text-sm font-medium transition-colors shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)]">
                    <div className="flex items-center justify-end gap-2">
                      {onManageSubDelegates && user.role !== 'SUB_DELEGATE' && (
                        <button
                          type="button"
                          onClick={() => onManageSubDelegates(user.id)}
                          className="p-1.5 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 rounded-md transition-colors"
                          aria-label={`Manage sub-delegates for ${displayName}`}
                          title="Manage sub-delegates"
                        >
                          <Users className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onEdit(user.id)}
                        className="p-1.5 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 rounded-md transition-colors"
                        aria-label={`Edit ${displayName}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(user.id)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        aria-label={`Delete ${displayName}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sticky Footer - Summary info */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
        <div>
          {users.length > 0 && (
            <span>
              Displaying {users.length} {users.length === 1 ? 'row' : 'rows'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>Scroll for more →</span>
        </div>
      </div>
    </div>
  );
}

